import React, { useEffect } from 'react';
import { Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import TaskDynamicFieldsInput from './Input/TasksDynamicFieldsInput';
import {
    DISABLE_DYNAMIC_FIELDS,
    fetchDynamicFields,
    isMaskValid,
    isRegexValid,
    isDynamicFieldValid,
    isUserViewer,
    isFieldTypeValid,
    FIELD_TYPES,
} from './utils/utils';
import './TaskDynamicFields.scss';
import { trackPromise } from 'react-promise-tracker';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { selectUser } from '../../../redux/user/user-selector';

// since state is managed in the parent, the state and setState functions
// must be passed as props to this component

// Required props:
// {
//     projectTypeId: int
//     taskTypeId: int
//     dynamicFields: DynamicFieldsDto[]
//     setDynamicFields: (dynamicFields: DynamicFieldsDto[]) => void
//     disableErrors: boolean
//     taskMembers: members field on workItem object
// }
const TaskDynamicFields = (props) => {
    const {
        projectTypeId,
        taskTypeId,
        dynamicFields,
        setDynamicFields,
        taskMembers,
        user,
        isFromCreate,
        disabled
    } = props;

    // disable fields for viewers
    const isViewer = isUserViewer(user, taskMembers);

    useEffect(() => {
        if (DISABLE_DYNAMIC_FIELDS) return;
        trackPromise(
            fetchDynamicFields({ projectTypeId, taskTypeId })
                .then(setDynamicFields)
                .catch((error) => {
                    setDynamicFields([]);
                    console.log(error);
                }),
            'tasks-comments-panel'
        );
    }, [projectTypeId, taskTypeId]);

    if (dynamicFields.length === 0 || DISABLE_DYNAMIC_FIELDS) return null;

    return (
        <div className="tasks-dynamic-fields-wrapper">
            {dynamicFields.map((field, idx) => {
                if (!field.Visible || field.Deleted) return null;

                // for field input
                function handleChange(e) {
                    setDynamicFields(
                        dynamicFields.map((oldField) =>
                            oldField.Id === field.Id
                                ? {
                                      ...oldField,
                                      Data: e.target.value,
                                      touched: true,
                                  }
                                : oldField
                        )
                    );
                }

                let fieldInputLabel = (
                    <Form.Label>
                        {field.Label}
                        <span className="red">{field.Required ? ' *' : null}</span>
                    </Form.Label>
                );

                if (field.FieldHelp) {
                    fieldInputLabel = (
                        <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>{field.FieldHelp}</Tooltip>}
                        >
                            {fieldInputLabel}
                        </OverlayTrigger>
                    );
                }

                let invalidFeedback = null;

                if (!isMaskValid(field)) {
                    invalidFeedback = 'Must follow the format: ' + field.FieldMask;
                } else if (!isRegexValid(field)) {
                    invalidFeedback = field.FieldRegexDescription;
                } else if (!isFieldTypeValid(field)) {
                    switch (field.FieldType) {
                        case FIELD_TYPES.time:
                            invalidFeedback =
                                'Hours and minutes must be within the range of 00:00 to 168:00';
                            break;
                        default:
                            invalidFeedback = `Invalid values for this type of input: ${field.FieldType}`;
                    }
                } else if (!isDynamicFieldValid(field) && !isFromCreate) {
                    invalidFeedback = 'Field is Required';
                }

                return (
                    <Form.Group
                        className={`${field.CssClass ?? ''} ${(isViewer && !isFromCreate) ? 'disabled' : ''}`}
                        key={idx}
                    >
                        {field.FieldType === FIELD_TYPES.toggle ? "" : fieldInputLabel}
                        <TaskDynamicFieldsInput
                            field={field}
                            handleChange={handleChange}
                            disabled={(isViewer && !isFromCreate) || disabled}
                            isFromCreate={isFromCreate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {invalidFeedback}
                        </Form.Control.Feedback>
                    </Form.Group>
                );
            })}
        </div>
    );
};

const mapState = createStructuredSelector({ user: selectUser });

export default connect(mapState)(TaskDynamicFields);
